import { Controller } from "@hotwired/stimulus";

const READY_ATTRIBUTE = "data-table-columns-ready";

/**
 * Lets a manager choose which columns of a list they see, and in what order.
 *
 * The server renders every column it knows how to fill, each cell tagged with its key.
 * This controller then hides what the user did not ask for and reorders what is left.
 * Doing it client-side keeps the server rendering one canonical table: a per-user column
 * set decided on the server would mean the markup differs between two people looking at
 * the same list, which is a poor thing to debug.
 *
 * Table cells cannot be reordered with CSS — `order` only applies inside flex and grid —
 * so the reordering moves the actual nodes. That is O(rows × columns) per change, which
 * is nothing at a page size of fifty and would need revisiting at ten thousand.
 *
 * The choice lives in localStorage rather than in the database. It is a display
 * preference we are still shaping, and a schema migration for it now would freeze a
 * format before we know what belongs in it. The cost is that the preference does not
 * follow the user to another machine.
 */
export default class extends Controller<HTMLElement> {
    static targets = ["headerRow", "body", "panel", "option"];
    static values = { storageKey: String, default: String };

    declare readonly headerRowTarget: HTMLTableRowElement;
    declare readonly bodyTargets: HTMLTableSectionElement[];
    declare readonly hasPanelTarget: boolean;
    declare readonly panelTarget: HTMLElement;
    declare readonly storageKeyValue: string;
    declare readonly defaultValue: string;

    private serverOrder: string[] = [];
    private order: string[] = [];
    private hidden = new Set<string>();
    private dragKey: string | null = null;
    private dragStartY = 0;
    private dragging = false;
    private observer: MutationObserver | null = null;
    private panelOpen = false;

    connect(): void {
        // Read before anything is moved: apply() reorders the header in place, so after the
        // first change the DOM no longer remembers what the server sent — and "reset" has
        // nothing to reset to.
        this.serverOrder = this.readHeaderKeys();

        const known = this.knownKeys();
        const stored = this.readStored();

        // A column added to the server since the preference was saved must still appear,
        // and one removed must not linger as a dead entry.
        const restored = stored ? [...stored.order.filter((key) => known.includes(key)), ...known.filter((key) => !stored.order.includes(key))] : known;
        // The anchor stays first whatever a stored preference says: the sticky first column
        // and the "cannot be hidden" rule both assume it is there.
        this.order = [this.anchorKey(), ...restored.filter((key) => key !== this.anchorKey())];
        this.hidden = stored ? new Set(stored.hidden.filter((key) => known.includes(key))) : new Set(known.filter((key) => !this.defaults().includes(key)));
        this.hidden.delete(this.anchorKey());

        this.apply();

        /* Watch our own markup rather than trusting an event.
         *
         * This list lives on an auto-refreshing page: the server's HTML is morphed back
         * over ours every time anything changes, which resets every cell's `hidden` and
         * every cell's position — and because the morph keeps the element, Stimulus never
         * reconnects to notice. Infinite scroll appends rows the same way. Which event
         * announces it depends on the refresh path taken, so the reliable signal is the
         * DOM itself.
         */
        this.observer = new MutationObserver(() => {
            if (!this.matchesChoice()) this.apply();
        });
        this.observer.observe(this.element, {
            childList: true,
            subtree: true,
            // `class` is in the list because the panel's open state lives in one, and a
            // morph that only closes the panel changes nothing else.
            attributeFilter: ["hidden", "class", READY_ATTRIBUTE],
        });
    }

    disconnect(): void {
        this.observer?.disconnect();
        this.observer = null;
    }

    /** Whether the rendered table already says what the manager asked for. */
    private matchesChoice(): boolean {
        // The marker is part of the state, not a report about it: a morph that rewrites the
        // table to the same columns still wipes it, and a test waiting on it would wait
        // forever while the table sat there looking perfectly correct.
        if (this.element.getAttribute(READY_ATTRIBUTE) !== "true") return false;
        if (this.hasPanelTarget && this.panelTarget.classList.contains("hidden") === this.panelOpen) return false;

        const rows: HTMLElement[] = [this.headerRowTarget];
        // One row per body is enough: they are all rewritten together.
        this.bodyTargets.forEach((body) => {
            const first = body.rows[0];
            if (first) rows.push(first as HTMLElement);
        });

        return rows.every((row) => {
            const cells = Array.from(row.children) as HTMLElement[];
            if (cells.length !== this.order.length) return false;
            return cells.every(
                (cell, position) => cell.dataset.col === this.order[position] && cell.hidden === this.hidden.has(this.order[position])
            );
        });
    }

    private readHeaderKeys(): string[] {
        return Array.from(this.headerRowTarget.children)
            .map((cell) => (cell as HTMLElement).dataset.col ?? "")
            .filter(Boolean);
    }

    /** The column that names the row. Pinned first, never hidden. */
    private anchorKey(): string {
        return this.knownKeys()[0] ?? "";
    }

    private knownKeys(): string[] {
        return this.serverOrder.length ? this.serverOrder : this.readHeaderKeys();
    }

    private defaults(): string[] {
        return this.defaultValue ? this.defaultValue.split(",").filter(Boolean) : this.knownKeys();
    }

    private storageName(): string {
        return `elron.table-columns.${this.storageKeyValue}`;
    }

    private readStored(): { order: string[]; hidden: string[] } | null {
        try {
            const raw = window.localStorage.getItem(this.storageName());
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed?.order) || !Array.isArray(parsed?.hidden)) return null;
            return { order: parsed.order, hidden: parsed.hidden };
        } catch {
            // A corrupt or unavailable store must not take the table down with it.
            return null;
        }
    }

    private persist(): void {
        try {
            window.localStorage.setItem(this.storageName(), JSON.stringify({ order: this.order, hidden: [...this.hidden] }));
        } catch {
            // Private browsing and full quotas both throw here. The table still works.
        }
    }

    /** Reorder and show/hide every row, including ones swapped in later by HTMX. */
    apply(): void {
        const rows: HTMLElement[] = [this.headerRowTarget];
        this.bodyTargets.forEach((body) => rows.push(...(Array.from(body.rows) as HTMLElement[])));

        rows.forEach((row) => {
            const byKey = new Map<string, HTMLElement>();
            Array.from(row.children).forEach((cell) => {
                const key = (cell as HTMLElement).dataset.col;
                if (key) byKey.set(key, cell as HTMLElement);
            });

            this.order.forEach((key) => {
                const cell = byKey.get(key);
                if (!cell) return;
                cell.hidden = this.hidden.has(key);
                row.appendChild(cell);
            });
        });

        this.syncPanel();
        this.syncPanelVisibility();

        // Says the stored preference has been folded into the server's markup. Without it
        // there is no way to tell "the columns you asked for" from "the columns the server
        // sent, a frame before the controller woke up".
        this.element.setAttribute(READY_ATTRIBUTE, "true");
    }

    togglePanel(event?: Event): void {
        event?.preventDefault();
        if (!this.hasPanelTarget) return;
        this.panelOpen = !this.panelOpen;
        this.syncPanelVisibility();
    }

    closePanel(event: MouseEvent): void {
        if (!this.panelOpen) return;
        const target = event.target;
        if (target instanceof Node && this.element.contains(target)) return;
        this.panelOpen = false;
        this.syncPanelVisibility();
    }

    /* Whether the panel is open is state, not markup.

    It used to live only in a class, which auto-refresh overwrote with the server's: a
    manager half-way through choosing columns watched the panel vanish under them, and the
    only clue was that it happened about a second after they opened it. Held here and
    re-asserted after every re-render, like the column choice itself.
    */
    private syncPanelVisibility(): void {
        if (!this.hasPanelTarget) return;
        this.panelTarget.classList.toggle("hidden", !this.panelOpen);
    }

    toggleColumn(event: Event): void {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        const key = input.dataset.col;
        if (!key) return;

        if (key === this.anchorKey()) {
            input.checked = true;
            return;
        }

        if (input.checked) this.hidden.delete(key);
        else this.hidden.add(key);

        this.persist();
        this.apply();
    }

    reset(event?: Event): void {
        event?.preventDefault();
        this.order = this.knownKeys();
        this.hidden = new Set(this.knownKeys().filter((key) => !this.defaults().includes(key)));
        this.hidden.delete(this.anchorKey());
        this.persist();
        this.apply();
    }

    /* Reordering runs on pointer events rather than the HTML5 drag-and-drop API.
     *
     * Native drag looks like the obvious choice and costs more than it gives: it does not
     * fire from a touch pointer at all, its drag image is not stylable, and a synthetic
     * pointer sequence does not produce it — so the whole feature would sit outside what a
     * browser test can exercise. Pointer events behave the same under a mouse, a finger and
     * a test.
     */
    reorderStart(event: PointerEvent): void {
        if (event.button !== 0) return;

        // Only the grip starts a reorder. The rest of the row is a label that toggles the
        // column, and a row that both drags and toggles makes every click a coin flip.
        const handle = (event.target as HTMLElement)?.closest("[data-drag-handle]");
        if (!handle) return;

        const item = handle.closest("li[data-col]");
        if (!(item instanceof HTMLElement)) return;

        this.dragKey = item.dataset.col ?? null;
        this.dragStartY = event.clientY;
        this.dragging = false;
    }

    reorderMove(event: PointerEvent): void {
        if (!this.dragKey) return;

        // A few pixels of slop so a press that wobbles stays a press.
        if (!this.dragging && Math.abs(event.clientY - this.dragStartY) < 4) return;
        event.preventDefault();

        const item = this.panelItems().find((candidate) => candidate.dataset.col === this.dragKey);
        if (!item) return;

        // Captured only once the gesture is unambiguously a drag, so a press that turns out
        // to be a click still reaches the control underneath it.
        if (!this.dragging) {
            this.dragging = true;
            item.setPointerCapture(event.pointerId);
        }
        item.dataset.dragging = "true";

        this.clearDropMarkers();
        const over = this.itemAt(event.clientX, event.clientY);
        if (over && over.dataset.col !== this.dragKey) over.dataset.dropTarget = "true";
    }

    reorderEnd(event: PointerEvent): void {
        const dragKey = this.dragKey;
        const wasDragging = this.dragging;
        const over = this.itemAt(event.clientX, event.clientY);
        this.cancelReorder();

        if (!dragKey || !wasDragging || !over) return;

        const targetKey = over.dataset.col;
        if (!targetKey || targetKey === dragKey || targetKey === this.anchorKey()) return;

        // Releasing over a column below the origin means "put it after that one"; inserting
        // before would leave it one short of where it was let go.
        const movingDown = this.order.indexOf(dragKey) < this.order.indexOf(targetKey);
        const next = this.order.filter((key) => key !== dragKey);
        next.splice(next.indexOf(targetKey) + (movingDown ? 1 : 0), 0, dragKey);
        this.order = next;

        this.persist();
        this.apply();
    }

    cancelReorder(): void {
        this.dragKey = null;
        this.dragging = false;
        this.clearDropMarkers();
        this.panelItems().forEach((item) => delete item.dataset.dragging);
    }

    private itemAt(x: number, y: number): HTMLElement | null {
        // elementsFromPoint rather than the event target: the pointer is captured by the
        // item being dragged, so every move reports that same item as the target.
        const hit = document.elementsFromPoint(x, y).find((element) => element instanceof HTMLElement && element.matches("li[data-col]"));
        return hit instanceof HTMLElement && this.element.contains(hit) ? hit : null;
    }

    private panelItems(): HTMLElement[] {
        if (!this.hasPanelTarget) return [];
        return Array.from(this.panelTarget.querySelectorAll<HTMLElement>("li[data-col]"));
    }

    private clearDropMarkers(): void {
        this.panelItems().forEach((item) => delete item.dataset.dropTarget);
    }

    private syncPanel(): void {
        if (!this.hasPanelTarget) return;

        const list = this.panelTarget.querySelector("[data-table-columns-list]");
        if (!(list instanceof HTMLElement)) return;

        const byKey = new Map<string, HTMLElement>();
        Array.from(list.children).forEach((item) => {
            const key = (item as HTMLElement).dataset.col;
            if (key) byKey.set(key, item as HTMLElement);
        });

        this.order.forEach((key) => {
            const item = byKey.get(key);
            if (!item) return;
            const input = item.querySelector("input[type=checkbox]");
            if (input instanceof HTMLInputElement) {
                input.checked = !this.hidden.has(key);
                input.disabled = key === this.anchorKey();
            }
            list.appendChild(item);
        });
    }
}
